/**
 * Word tokens for both sides of `matchesSearchTerms`: diacritics folded,
 * lowercased, split on runs of non-letter/non-digit (Unicode-aware, so
 * "Café" → ["cafe"] and "上海 商店" tokenizes into real words instead of
 * vanishing). Applying ONE tokenizer to both the text and the query is the
 * point — punctuation typed in a query must be a separator, not a token that
 * can never match a stripped text word (a previous version split the query on
 * whitespace only, so "goodreads - " kept "-" as its own token and could
 * never match anything).
 */
const toWords = (value: string): string[] =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)

/**
 * True when EVERY word in `searchTerm` matches the START of some word in
 * `text` (case- and diacritic-insensitive). "Mango & Pineapple" tokenizes to
 * ["mango", "pineapple"]; punctuation in either string is a word boundary, not
 * a token. Prefix (not substring) matching is deliberate: it keeps "twin" →
 * "Twinings" while rejecting "ad" → "lady".
 */
export const matchesSearchTerms = (
  text: string,
  searchTerm: string,
): boolean => {
  if (!searchTerm) return true
  const textWords = toWords(text)
  return toWords(searchTerm).every((word) =>
    textWords.some((textWord) => textWord.startsWith(word)),
  )
}
